import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowDownRight, Copy, Check, Lock, Flame } from 'lucide-react';
import Particles from '@/components/react-bits/particles';
import GlitchText from '@/components/react-bits/glitch-text';
import ShinyText from '@/components/react-bits/shiny-text';
import { trackEvent } from '@/lib/analytics';

const CTA = [
  { label: 'The Legend', href: '#about', primary: true },
  { label: 'Meme Lab', href: '#memes', primary: false },
] as const;

export default function Hero() {
  const [copied, setCopied] = useState(false);
  const CA = "BAdGeRm3k4o8xQ5HhP9gLx1vR7Z8Kq9ZfV5Nm2w1YyU";

  const copyCA = () => {
    navigator.clipboard.writeText(CA);
    setCopied(true);
    trackEvent('copy_ca', { location: 'hero' });
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section
      id="hero"
      className="relative flex items-center overflow-hidden"
      style={{ minHeight: '62vh', paddingTop: 'clamp(7.5rem, 12vw, 10rem)', paddingBottom: 'clamp(4rem, 8vw, 5.5rem)' }}
    >
      {/* Background image */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: 'url(/assets/background.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      />
      {/* Dark overlay so content reads cleanly */}
      <div className="absolute inset-0" style={{ background: 'rgba(8,8,8,0.72)' }} />
      {/* Bottom fade into next section */}
      <div className="absolute bottom-0 left-0 right-0" style={{ height: '30%', background: 'linear-gradient(to bottom, transparent, #080808)' }} />

      <Particles count={50} className="opacity-40" />
      <div className="noise-overlay" />

      {/* Two-column layout */}
      <div className="relative z-10 w-full px-[clamp(1.25rem,9vw,15%)] flex flex-col md:flex-row items-center gap-10 md:gap-16">

        {/* LEFT — Honey Badger image */}
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="flex-shrink-0 flex justify-center"
          style={{ width: 'clamp(240px, 35%, 420px)' }}
        >
          <motion.div
            className="relative"
            initial={{ scale: 0.94, rotate: -1.4 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ duration: 0.65, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
            style={{ filter: 'drop-shadow(0 0 48px rgba(212,175,55,0.4)) drop-shadow(0 0 100px rgba(212,175,55,0.15))', marginTop: 'clamp(0.8rem, 4vw, 3rem)' }}
          >
            {/* Panel behind */}
            <img
              src="/assets/panel_v2.png"
              alt=""
              loading="eager"
              fetchPriority="high"
              decoding="async"
              style={{ width: '100%', height: 'auto', objectFit: 'contain', display: 'block' }}
            />
            {/* Badger on top, centered inside panel */}
            <img
              src="/assets/pfp-v2.png"
              alt="Honey Badger"
              loading="eager"
              fetchPriority="high"
              decoding="async"
              style={{
                position: 'absolute',
                bottom: '2%',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '98%',
                height: 'auto',
                objectFit: 'contain',
              }}
            />
          </motion.div>
        </motion.div>

        {/* RIGHT — Content */}
        <div className="flex-1 flex flex-col items-start text-left w-full">

          {/* Chain badge */}
          <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mb-6">
            <span className="text-label tracking-[0.3em]" style={{ color: '#D4AF37' }}>◆ &nbsp; SOLANA &nbsp; ◆</span>
          </motion.div>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="text-display font-black mb-3"
            style={{ lineHeight: 0.9 }}
          >
            <GlitchText text="HONEY BADGER" className="text-gold text-glow" />
          </motion.h1>

          {/* Ticker */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }} className="mb-4">
            <ShinyText className="text-title tracking-[0.2em] font-bold">$BADGER</ShinyText>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="mb-5 h-[2px] w-[min(220px,48vw)]"
            style={{ background: 'linear-gradient(90deg, rgba(212,175,55,0.95) 0%, rgba(212,175,55,0.2) 75%, transparent 100%)' }}
          />

          {/* Tagline */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mb-8"
          >
            <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 'clamp(0.95rem, 3.8vw, 1.05rem)', fontWeight: 500, lineHeight: 1.65, maxWidth: 420 }}>
              Bear markets, FUD, rugs — he's seen it all.{' '}
              <span style={{ color: '#D4AF37', fontWeight: 700 }}>He just wants the honey.</span>
            </p>
            <p style={{ color: 'rgba(255,255,255,0.32)', fontSize: 'clamp(0.76rem, 2.8vw, 0.85rem)', fontWeight: 500, marginTop: 10, letterSpacing: '0.08em' }}>
              0% tax. Liquidity locked. Mint revoked.{' '}
              <span style={{ color: 'rgba(212,175,55,0.75)', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Built to last.</span>
            </p>
          </motion.div>

          {/* CA Pill & Trust Signals */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            className="mb-8 flex items-center gap-4 sm:gap-6 flex-wrap"
          >
            {/* Copy CA Button */}
            <button
              onClick={copyCA}
              className="group flex items-center gap-2 px-4 py-2 transition-all duration-200 cursor-pointer"
              style={{
                background: 'rgba(20,20,20,0.6)',
                border: '1px solid rgba(212,175,55,0.25)',
                backdropFilter: 'blur(12px)',
                clipPath: 'polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%)',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(212,175,55,0.7)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(212,175,55,0.25)'; }}
            >
              <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.1em' }}>CA:</span>
              <span style={{ color: '#D4AF37', fontSize: '0.85rem', fontWeight: 700, letterSpacing: '0.05em' }} className="font-mono">
                {CA.slice(0, 6)}...{CA.slice(-6)}
              </span>
              {copied ? <Check size={14} color="#D4AF37" /> : <Copy size={14} color="rgba(255,255,255,0.4)" className="group-hover:text-[#D4AF37] transition-colors" />}
            </button>

            {/* Trust Signals */}
            <div className="flex items-center gap-4 border-l border-[rgba(212,175,55,0.15)] pl-4 py-1">
              <div className="flex items-center gap-1.5" style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                <Lock size={12} color="#D4AF37" /> LP Locked
              </div>
              <div className="flex items-center gap-1.5" style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                <Flame size={12} color="#D4AF37" /> Mint Revoked
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex flex-wrap gap-2 sm:gap-3"
          >
            {CTA.map((item) => (
              <a
                key={item.label}
                href={item.href}
                onClick={() => trackEvent('hero_cta_click', { label: item.label, target: item.href })}
                className="group inline-flex items-center gap-2 px-4 sm:px-5 py-3 sm:py-3 transition-all duration-200"
                style={item.primary
                  ? {
                    color: '#080808',
                    background: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 100%)',
                    clipPath: 'polygon(12px 0%, 100% 0%, calc(100% - 12px) 100%, 0% 100%)',
                    boxShadow: '0 8px 24px rgba(212,175,55,0.22)',
                    minHeight: 44,
                  }
                  : {
                    color: '#D4AF37',
                    border: '1px solid rgba(212,175,55,0.35)',
                    background: 'rgba(212,175,55,0.07)',
                    clipPath: 'polygon(12px 0%, 100% 0%, calc(100% - 12px) 100%, 0% 100%)',
                    minHeight: 44,
                  }}
              >
                <span className="text-label" style={{ letterSpacing: '0.11em' }}>{item.label}</span>
                <ArrowDownRight size={15} />
              </a>
            ))}
          </motion.div>

        </div>
      </div>

      <motion.a
        href="#about"
        onClick={() => trackEvent('hero_scroll_click', { target: '#about' })}
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 1], y: [0, 8, 0] }}
        transition={{
          opacity: { delay: 0.9, duration: 0.6 },
          y: { delay: 1.5, duration: 1.8, repeat: Infinity, ease: 'easeInOut' },
        }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 text-label"
        style={{ color: 'rgba(212,175,55,0.72)', letterSpacing: '0.18em' }}
      >
        SCROLL
      </motion.a>
    </section>
  );
}
